"use client"

import { clearTimeout } from "node:timers"
import React, { ReactNode, useEffect, useState } from "react"
import Link from "next/link"
import { useUbBottomRightPopupState } from "@/store/ubBottomRightPopupState"
import { CaretDownIcon, CaretUpIcon } from "@radix-ui/react-icons"
import Cookies from "js-cookie"
import { useIsMounted } from "usehooks-ts"

import {
  BottomRightPopupCookieValue,
  UB_BOTTOM_RIGHT_POPUP_COLLAPSED,
  UB_BOTTOM_RIGHT_POPUP_INTRO_ANIMATION_DONE,
} from "@/lib/bottomRightPopup"
import { UB_CUSTOMER_TYPE_COOKIE_NAME } from "@/lib/customer-type-cookies"
import { Button } from "@/components/ui/components"

import { cn } from "../ui/helpers/utils"

export interface BottomRightPopupData {
  title?: string
  body: ReactNode
  buttonText: string
  buttonUrl: string
  imageUrl?: string
}

export const BottomRightPopupClient: React.FC<Omit<React.HTMLProps<HTMLDivElement>, "children">> = ({
  className,
  ...props
}) => {
  const { currentPopupData, currentPopupShow, currentPopupCollapsed, setCurrentPopupCollapsed } =
    useUbBottomRightPopupState()
  const [temporaryCollapsed, setTemporaryCollapsed] = useState(currentPopupCollapsed)
  const [iccDialogWasVisible, setICcDialogWasVisible] = useState(false)
  const [wasIntroDone, setWasIntroDone] = useState(false)
  const [cookiesLoaded, setCookiesLoaded] = useState(false)
  const [introTimeout, setIntroTimeout] = useState<any>(undefined)
  const isMounted = useIsMounted()
  const INTRO_TIMEOUT = 3000

  function collapseBtnClick() {
    const newState = !temporaryCollapsed
    Cookies.set(UB_BOTTOM_RIGHT_POPUP_COLLAPSED, newState ? "true" : "false")
    setCurrentPopupCollapsed(newState)
    setTemporaryCollapsed(newState)
    // failsafe for intro timeout
    Cookies.set(UB_BOTTOM_RIGHT_POPUP_INTRO_ANIMATION_DONE, "true")
    setWasIntroDone(true)
  }

  function doIntro() {
    // console.debug("doIntro", iccDialogWasVisible)
    if (!iccDialogWasVisible) {
      return
    }
    setTemporaryCollapsed(false)
    setTimeout(() => {
      Cookies.set(UB_BOTTOM_RIGHT_POPUP_INTRO_ANIMATION_DONE, "true")
      try {
        if (typeof setIntroTimeout != "undefined") {
          setWasIntroDone(true)
        }
      } catch (_) {}
    }, 3500)
  }

  function stopIntroTimer(id) {
    // console.debug("stopIntroTimer()")
    try {
      clearTimeout(id)
    } catch (_) {}
  }
  function startIntroTimer() {
    // console.debug("startIntroTimer()", iccDialogWasVisible)
    if (iccDialogWasVisible) {
      setTemporaryCollapsed(true)
      const id = setTimeout(() => doIntro(), INTRO_TIMEOUT)
      setIntroTimeout(id)
      return () => stopIntroTimer(id)
    }
  }

  useEffect(() => {
    // console.debug("Loading cookies")
    let cookieVal = Cookies.get(UB_BOTTOM_RIGHT_POPUP_COLLAPSED)
    let collapsed = temporaryCollapsed
    if (cookieVal !== undefined) {
      // if we got this cookie we showed intro already
      stopIntroTimer(introTimeout)
      setWasIntroDone(true)

      collapsed = BottomRightPopupCookieValue.isBottomRightPopupCollapsed(cookieVal)
      setCurrentPopupCollapsed(collapsed)
      setTemporaryCollapsed(collapsed)
    }

    cookieVal = Cookies.get(UB_BOTTOM_RIGHT_POPUP_INTRO_ANIMATION_DONE)
    if (cookieVal !== undefined) {
      const done = BottomRightPopupCookieValue.wasBottomRightPopupIntroAnimationDone(cookieVal)
      setWasIntroDone(done)
      if (done) {
        stopIntroTimer(introTimeout)
        setTemporaryCollapsed(collapsed)
      } else {
        setTemporaryCollapsed(true)
      }
    }

    cookieVal = Cookies.get(UB_CUSTOMER_TYPE_COOKIE_NAME)
    if (cookieVal !== undefined) {
      setICcDialogWasVisible(true)
    } else {
      setTemporaryCollapsed(true)
      setWasIntroDone(false)
    }

    setCookiesLoaded(true)
    // console.debug("Loading cookies - done")

    const handleScroll = () => {
      if (window.scrollY >= window.document.body.offsetHeight / 10) {
        setTemporaryCollapsed(true)
      }
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // console.debug("useMounted", cookiesLoaded, !wasIntroDone, iccDialogWasVisible, isMounted())
    if (cookiesLoaded && !wasIntroDone && iccDialogWasVisible && isMounted()) {
      return startIntroTimer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, wasIntroDone, cookiesLoaded, iccDialogWasVisible])

  return (
    <>
      {currentPopupData && currentPopupShow && (
        <div
          className={cn("fixed right-40 bottom-0 z-210 flex size-fit max-h-96 flex-col max-lg:hidden", className, {
            "transition-[max-height_height] duration-3000 ease-in-out": !wasIntroDone,
            "max-h-11": temporaryCollapsed,
          })}
          {...props}
        >
          {currentPopupData?.imageUrl && (
            <div
              className={cn("absolute top-0 left-4 z-10 size-16 overflow-hidden rounded-xl drop-shadow-sm", {
                hidden: temporaryCollapsed,
              })}
            >
              {/* TODO use <StoryblockAsset> */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={currentPopupData.imageUrl} alt="" className={"size-16 object-cover object-center"} />
            </div>
          )}
          <div
            className={cn("group absolute inset-x-3 top-3 flex cursor-pointer justify-end", {
              "top-11": currentPopupData?.imageUrl && !temporaryCollapsed,
              "top-3": temporaryCollapsed,
            })}
            onClick={collapseBtnClick}
          >
            <Button
              type={"button"}
              variant={"ghost"}
              size={"icon"}
              className={cn("size-5! rounded-full bg-gray-50 group-hover:bg-primary-hover group-hover:text-white")}
            >
              {temporaryCollapsed ? <CaretUpIcon className={"size-5"} /> : <CaretDownIcon className={"size-5"} />}
            </Button>
          </div>
          <div
            className={cn(
              `
                flex min-h-10 min-w-11 flex-col gap-3 rounded-t-lg border border-b-0 border-gray-300 bg-white p-4
                shadow-sm
              `,
              {
                "mt-8 pt-9": currentPopupData?.imageUrl && !temporaryCollapsed,
                "flex-row p-2 pt-2 pl-3": temporaryCollapsed,
                "pr-10": currentPopupData.title && temporaryCollapsed,
              }
            )}
          >
            {currentPopupData.title && (
              <span
                className={cn("text-xl font-medium", {
                  "font-regular line-clamp-1 text-base text-ellipsis": temporaryCollapsed,
                })}
              >
                {currentPopupData.title}
              </span>
            )}
            <div
              className={cn("w-fit max-w-60 text-sm text-wrap text-ellipsis hyphens-auto", {
                hidden: temporaryCollapsed,
                "pt-1": !currentPopupData.title && currentPopupData.imageUrl,
                "pt-5": !currentPopupData.title && !currentPopupData.imageUrl,
              })}
            >
              {currentPopupData.body}
            </div>
            <Button asChild={true} variant={"default"} size={"default"} className={cn({ hidden: temporaryCollapsed })}>
              <Link prefetch={false} href={currentPopupData.buttonUrl} target={"_blank"}>
                {currentPopupData.buttonText}
              </Link>
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
